import { confirmPath } from "@/api/privateRequests/players/evolved-players/confirmPath";
import {
  PossiblePaths,
  getPossiblePaths,
} from "@/api/privateRequests/players/evolved-players/getPossiblePaths";
import { sendErrorReport } from "@/api/privateRequests/players/evolved-players/sendErrorReport";
import { Button } from "@/components/atoms/Buttons/Button";
import { RadioButton } from "@/components/atoms/RadioButton.tsx/RadioButton";
import {
  SquadBuilderPlayer,
  SquadBuilderPlayerProps,
} from "@/components/molecules/Player/SquadBuilderPlayer/SquadBuilderPlayer";
import { EvolutionPathDash } from "@/components/organisms/Players/MrDbListCardView/Sections/EvolutionPath";
import { EvolutionPathNode } from "@/components/organisms/Players/MrDbListCardView/Sections/EvolutionPath";
import { checkbox, closingCross } from "@/designSystemIcons";
import { useEscape } from "@/hooks/useKeyboardShortcut";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useScrollLock } from "@/hooks/useScrollLock";
import { simpleInfoIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { addAlert } from "@/redux/message/messageSlice";
import { updateMyEvolvedPlayersStatus } from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { Fragment, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useDispatch } from "react-redux";

import ActionModal from "../ActionModal";

export type MyEvolutionsActionModalState =
  | "needs-confirmation"
  | "needs-report";
type Props = {
  state: MyEvolutionsActionModalState;
  player: SquadBuilderPlayerProps;
  onDismiss: () => void;
};

const WhyDoesThisHappen = ({ onUnderstood }: { onUnderstood: () => void }) => {
  return (
    <div className="w-full h-full flex flex-col text-left">
      <div className="flex flex-col gap-y-4">
        <h3 className="text-xl font-semibold">Why does this happen?</h3>
        <p className="text-gray-300">
          FC 25 introduced some major changes to evolutions with the
          introduction of max stat rewards. Due to this update EA has slightly
          changed how in-game attributes are rewarded, which means that our
          calculation for automatically detecting your evolution chain in some
          rare cases cannot find the correct evolutions. This issue will
          continue to occur until we are able to apply the in-game attributes
          rewards in the exact same way as EA.
        </p>
      </div>
      <div className="flex justify-center desktop:justify-end mt-auto">
        <Button variant="tertiary" onClick={onUnderstood}>
          Understood
        </Button>
      </div>
    </div>
  );
};
const SelectBetweenEvolutionPaths = ({
  evoPathId,
  onDismiss,
  onConfirm,
  onHandleWhyDoesThisHappen,
  handleReport,
}: {
  evoPathId: number;
  onDismiss: () => void;
  onConfirm: (evos: number[]) => void;
  onHandleWhyDoesThisHappen: () => void;
  handleReport: () => void;
}) => {
  const [possiblePaths, setPossiblePaths] = useState<PossiblePaths>([]);
  const [selectedEvoPathIdx, setSelectedEvoPathIdx] =
    useState<number>(undefined);

  useEffect(() => {
    const fetchPaths = async () => {
      const res = await getPossiblePaths(evoPathId);
      setPossiblePaths(res);
    };
    fetchPaths();
  }, [evoPathId]);

  return (
    <div className="w-full h-full flex flex-col text-left ">
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-col items-center desktop:items-start">
          <h3 className="desktop:text-xl font-semibold ">
            Select evolution path used for this card
          </h3>
          <button
            className="flex flex-row items-center gap-x-1 text-dark-disabled cursor-pointer text-sm"
            onClick={onHandleWhyDoesThisHappen}
          >
            <span className="w-3 h-3">{simpleInfoIcon}</span>
            <span className="text-sm desktop:text-base ">
              Why does this happen?
            </span>
          </button>
        </div>
        <div className="flex flex-col gap-y-2 overflow-y-auto overflow-x-auto max-w-[550px] max-h-[250px] desktop:max-h-[160px]">
          {possiblePaths.map((path, idx) => (
            <RadioButton
              key={idx}
              label={
                <div className="flex flex-row">
                  {path.map((p, i) => (
                    <Fragment key={p.id}>
                      <EvolutionPathNode>{p.name}</EvolutionPathNode>

                      {i < path.length - 1 && <EvolutionPathDash />}
                    </Fragment>
                  ))}
                </div>
              }
              selected={selectedEvoPathIdx === idx}
              select={() => {
                selectedEvoPathIdx === idx
                  ? setSelectedEvoPathIdx(undefined)
                  : setSelectedEvoPathIdx(idx);
              }}
            />
          ))}
        </div>
      </div>
      <div className="flex justify-end mt-8 desktop:mt-auto gap-x-2 desktop:w-[200px] desktop:ml-auto">
        <Button variant="primary" size="large" onClick={onDismiss}>
          Cancel
        </Button>
        <Button
          variant="tertiary"
          size="large"
          disabled={selectedEvoPathIdx === undefined}
          onClick={() => {
            if (selectedEvoPathIdx !== undefined) {
              onConfirm(possiblePaths[selectedEvoPathIdx].map((p) => p.id));
            }
          }}
        >
          Confirm
        </Button>
      </div>
      <div className="desktop:absolute bottom-2 desktop:left-1/2 desktop:-translate-x-1/2 mt-8 mobile:mt-0">
        <span className="text-xs text-gray-300">
          None of the above? Click{" "}
          <span
            className="text-primary-blue underline cursor-pointer"
            onClick={handleReport}
          >
            here
          </span>{" "}
          to report
        </span>
      </div>
    </div>
  );
};

const ReportMissingEvolution = ({
  onDismiss,
  onReport,
  onHandleWhyDoesThisHappen,
  onHandleIDontRemember,
  selectedIDontRemember,
}: {
  onDismiss: () => void;
  onReport: (message: string, cantRememberPath: boolean) => void;
  onHandleWhyDoesThisHappen: () => void;
  onHandleIDontRemember: () => void;
  selectedIDontRemember: boolean;
}) => {
  const [message, setMessage] = useState("");
  return (
    <div className="w-full h-full flex flex-col text-left">
      <div className="flex flex-col gap-y-4 text-center mobile:text-left">
        <div className="flex flex-col">
          <h3 className="desktop:text-xl font-semibold">
            Report missing evolution path
          </h3>
          <button
            className="flex flex-row items-center gap-x-1 text-dark-disabled cursor-pointer text-sm justify-center desktop:justify-start"
            onClick={onHandleWhyDoesThisHappen}
          >
            <span className="w-3 h-3">{simpleInfoIcon}</span>
            <span className="text-sm desktop:text-base">
              Why does this happen?
            </span>
          </button>
        </div>
        <div className="flex flex-col gap-y-2 text-xs desktop:text-base">
          <h4>Describe the correct evolution path (Optional)</h4>
          <textarea
            className="text-gray-700 p-2 rounded-md h-32 desktop:h-20 max-h-32 text-base bg-white/80"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <button
          className={cn(
            "flex flex-row pl-2 pr-3 py-1 bg-white/[0.2] hover:bg-white/[0.3] rounded-lg w-fit gap-x-2 justify-between items-center whitespace-nowrap text-secondary",
          )}
          onClick={onHandleIDontRemember}
        >
          <span>{checkbox(selectedIDontRemember)}</span>
          <span className="text-xs">
            I don't remember the evolution path for this card
          </span>
        </button>
      </div>
      <div className="flex justify-end desktop:mt-auto mt-8 gap-x-2 desktop:w-[200px] desktop:ml-auto">
        <Button variant="primary" size="large" onClick={onDismiss}>
          Cancel
        </Button>
        <Button
          variant="tertiary"
          size="large"
          onClick={() => onReport(message, selectedIDontRemember)}
        >
          Report
        </Button>
      </div>
    </div>
  );
};

const MyEvolutionsActionModal = ({
  state: propState,
  player,
  onDismiss,
}: Props) => {
  const ref = useOutsideClick(() => {
    onDismiss();
  });
  useEscape(() => {
    onDismiss();
  });
  type localState = Props["state"] | "why-does-this-happen";
  const dispatch = useDispatch<AppDispatch>();
  const [state, setState] = useState<localState>(propState);
  const [previousState, setPreviousState] = useState<localState>();
  const [selectedIDontRemember, setSelectedIDontRemember] = useState(false);

  // Lock scroll on mobile devices
  useScrollLock(isMobile);

  useEffect(() => {
    if (!isMobile) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const handleOnConfirm = async (selectedEvoPath: number[]) => {
    const res = await confirmPath(player.player.evoPathId, selectedEvoPath);
    dispatch(
      updateMyEvolvedPlayersStatus([
        {
          evoPathId: player.player.evoPathId,
          status: res.loading ? "LOADING" : "MATCHED",
          availableEvos: res.availableEvos,
        },
      ]),
    );
    dispatch(
      addAlert({
        description: "Evolution path confirmed",
        type: "success",
      }),
    );
    onDismiss();
  };

  const handleOnReport = async (message: string, cantRememberPath: boolean) => {
    await sendErrorReport(player.player.evoPathId, message, cantRememberPath);

    dispatch(
      updateMyEvolvedPlayersStatus([
        {
          evoPathId: player.player.evoPathId,
          status: "REPORT_SENT",
          availableEvos: 0,
        },
      ]),
    );

    dispatch(
      addAlert({
        description: "Error report sent",
        type: "success",
      }),
    );
    onDismiss();
  };

  return (
    <ActionModal onDismiss={onDismiss}>
      <section className="flex flex-col desktop:flex-row gap-x-8 gap-y-8 items-center justify-center h-full pt-12 mobile:pt-0">
        <div className="w-[140px] h-[200px] scale-[175%] flex items-center justify-center desktop:scale-125">
          <SquadBuilderPlayer {...player} />
        </div>
        {state === "why-does-this-happen" ? (
          <WhyDoesThisHappen
            onUnderstood={() => {
              setState(previousState);
              setPreviousState(undefined);
            }}
          />
        ) : state === "needs-confirmation" ? (
          <SelectBetweenEvolutionPaths
            evoPathId={player.player.evoPathId}
            handleReport={() => {
              setPreviousState(state);
              setState("needs-report");
            }}
            onHandleWhyDoesThisHappen={() => {
              setPreviousState(state);
              setState("why-does-this-happen");
            }}
            onDismiss={onDismiss}
            onConfirm={handleOnConfirm}
          />
        ) : (
          state === "needs-report" && (
            <ReportMissingEvolution
              onDismiss={onDismiss}
              onReport={handleOnReport}
              onHandleWhyDoesThisHappen={() => {
                setPreviousState(state);
                setState("why-does-this-happen");
              }}
              onHandleIDontRemember={() =>
                setSelectedIDontRemember((prev) => !prev)
              }
              selectedIDontRemember={selectedIDontRemember}
            />
          )
        )}
      </section>
      {previousState && (
        <button
          className="absolute top-4 left-4 p-2 fill-secondary-dark flex gap-x-1 text-xs"
          onClick={() => {
            setState(previousState);
            setPreviousState(undefined);
          }}
        >
          <span>{"<-"}</span>
          <span>Back</span>
        </button>
      )}
    </ActionModal>
  );
};

export default MyEvolutionsActionModal;
